import React from 'react';
import { motion } from 'framer-motion';
import ModernHeader from '../../components/modern/ModernHeader';
import ModernFooter from '../../components/modern/ModernFooter';
import SEO from '../../components/modern/SEO';

export default function ModernAdatkezeles() {
  return (
    <>
      <SEO 
        title="Adatkezelési tájékoztató - XL bútor"
        description="XL bútor adatvédelmi nyilatkozat és adatkezelési tájékoztató. GDPR megfelelés, személyes adatok kezelése."
      />
      
      <div className="bg-[var(--color-bg)]">
        <ModernHeader />
        
        <main>
          {/* Hero Section */}
          <section className="relative py-20 md:py-28 bg-gradient-to-br from-[var(--color-text-primary)] to-gray-800">
            <div className="mx-auto max-w-[var(--container-max)] px-4 text-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <h1 className="text-4xl md:text-5xl font-[var(--font-serif)] font-semibold text-white mb-6 tracking-tight">
                  Adatkezelési tájékoztató
                </h1>
                <p className="text-lg md:text-xl text-white/80 max-w-2xl mx-auto">
                  Személyes adatok kezelése és védelme
                </p>
              </motion.div>
            </div>
          </section>

          {/* Content Section */}
          <section className="py-16 md:py-24 bg-white">
            <div className="mx-auto max-w-[900px] px-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="space-y-12"
              >
                {/* 1. Adatkezelő adatai */}
                <div>
                  <h2 className="text-2xl md:text-3xl font-[var(--font-serif)] font-semibold text-[var(--color-text-primary)] mb-6">
                    1. Adatkezelő adatai
                  </h2>
                  <div className="bg-[var(--color-bg)] rounded-2xl p-8 shadow-[var(--shadow-subtle)]">
                    <div className="space-y-3">
                      <p><strong className="text-[var(--color-text-primary)]">Név:</strong> <span className="text-[var(--color-text-secondary)]">XL Bútor és Szőnyeg</span></p>
                      <p><strong className="text-[var(--color-text-primary)]">Székhely:</strong> <span className="text-[var(--color-text-secondary)]">4600 Kisvárda, Attila u. 61.</span></p>
                      <p><strong className="text-[var(--color-text-primary)]">E-mail:</strong> <a href="mailto:xlbutorbolt@gmail.com" className="text-[var(--color-accent)] hover:underline">xlbutorbolt@gmail.com</a></p>
                      <p><strong className="text-[var(--color-text-primary)]">Telefon:</strong> <a href="tel:06704179210" className="text-[var(--color-accent)] hover:underline">06 (70) 417 9210</a></p>
                    </div>
                  </div>
                </div>

                {/* 2. Kezelt személyes adatok */}
                <div>
                  <h2 className="text-2xl md:text-3xl font-[var(--font-serif)] font-semibold text-[var(--color-text-primary)] mb-4">
                    2. Kezelt személyes adatok
                  </h2>
                  <p className="text-[var(--color-text-secondary)] leading-relaxed mb-4">
                    Weboldalunkon a következő személyes adatokat kezeljük:
                  </p>
                  <ul className="space-y-3 text-[var(--color-text-secondary)]">
                    <li className="flex items-start gap-3">
                      <span className="text-[var(--color-accent)] text-xl flex-shrink-0">•</span>
                      <span>Név és elérhetőségi adatok (e-mail cím, telefonszám)</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-[var(--color-accent)] text-xl flex-shrink-0">•</span>
                      <span>Számlázási és szállítási címek</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-[var(--color-accent)] text-xl flex-shrink-0">•</span>
                      <span>Rendelési információk és előzmények</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-[var(--color-accent)] text-xl flex-shrink-0">•</span>
                      <span>Weboldal látogatási adatok (cookies, IP-cím)</span>
                    </li>
                  </ul>
                </div>

                {/* 3. Adatkezelés célja és jogalapja */}
                <div>
                  <h2 className="text-2xl md:text-3xl font-[var(--font-serif)] font-semibold text-[var(--color-text-primary)] mb-4">
                    3. Adatkezelés célja és jogalapja
                  </h2>
                  <p className="text-[var(--color-text-secondary)] leading-relaxed mb-4">
                    Adatkezelésünk céljai és jogalapjai:
                  </p>
                  <ul className="space-y-3 text-[var(--color-text-secondary)]">
                    <li className="flex items-start gap-3">
                      <span className="text-[var(--color-accent)] text-xl flex-shrink-0">•</span>
                      <span><strong className="text-[var(--color-text-primary)]">Rendelés teljesítése:</strong> szerződés teljesítése (GDPR 6. cikk (1) bekezdés b) pont)</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-[var(--color-accent)] text-xl flex-shrink-0">•</span>
                      <span><strong className="text-[var(--color-text-primary)]">Számlázás:</strong> könyvviteli kötelezettség teljesítése (GDPR 6. cikk (1) bekezdés c) pont)</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-[var(--color-accent)] text-xl flex-shrink-0">•</span>
                      <span><strong className="text-[var(--color-text-primary)]">Marketing kommunikáció:</strong> hozzájárulás alapján (GDPR 6. cikk (1) bekezdés a) pont)</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-[var(--color-accent)] text-xl flex-shrink-0">•</span>
                      <span><strong className="text-[var(--color-text-primary)]">Weboldal működtetése:</strong> jogos érdek (GDPR 6. cikk (1) bekezdés f) pont)</span>
                    </li>
                  </ul>
                </div>

                {/* 4. Adatok tárolási ideje */}
                <div>
                  <h2 className="text-2xl md:text-3xl font-[var(--font-serif)] font-semibold text-[var(--color-text-primary)] mb-4">
                    4. Adatok tárolási ideje
                  </h2>
                  <ul className="space-y-3 text-[var(--color-text-secondary)]">
                    <li className="flex items-start gap-3">
                      <span className="text-[var(--color-accent)] text-xl flex-shrink-0">•</span>
                      <span><strong className="text-[var(--color-text-primary)]">Rendelési adatok:</strong> 8 év (könyvviteli kötelezettség)</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-[var(--color-accent)] text-xl flex-shrink-0">•</span>
                      <span><strong className="text-[var(--color-text-primary)]">Marketing adatok:</strong> hozzájárulás visszavonásáig</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-[var(--color-accent)] text-xl flex-shrink-0">•</span>
                      <span><strong className="text-[var(--color-text-primary)]">Weboldal látogatási adatok:</strong> 2 év</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-[var(--color-accent)] text-xl flex-shrink-0">•</span>
                      <span><strong className="text-[var(--color-text-primary)]">Cookie-k:</strong> a cookie típusától függően</span>
                    </li>
                  </ul>
                </div>

                {/* 5. Adatok továbbítása */}
                <div>
                  <h2 className="text-2xl md:text-3xl font-[var(--font-serif)] font-semibold text-[var(--color-text-primary)] mb-4">
                    5. Adatok továbbítása
                  </h2>
                  <p className="text-[var(--color-text-secondary)] leading-relaxed mb-4">
                    Személyes adatait csak a szolgáltatásunk teljesítéséhez szükséges harmadik feleknek adjuk át:
                  </p>
                  <ul className="space-y-3 text-[var(--color-text-secondary)]">
                    <li className="flex items-start gap-3">
                      <span className="text-[var(--color-accent)] text-xl flex-shrink-0">•</span>
                      <span>Futárszolgálatok (csomagküldéshez)</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-[var(--color-accent)] text-xl flex-shrink-0">•</span>
                      <span>Fizetési szolgáltatók (fizetés feldolgozásához)</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-[var(--color-accent)] text-xl flex-shrink-0">•</span>
                      <span>Könyvelő iroda (könyvviteli feladatokhoz)</span>
                    </li>
                  </ul>
                </div>

                {/* 6. Érintett jogai */}
                <div>
                  <h2 className="text-2xl md:text-3xl font-[var(--font-serif)] font-semibold text-[var(--color-text-primary)] mb-4">
                    6. Érintett jogai
                  </h2>
                  <p className="text-[var(--color-text-secondary)] leading-relaxed mb-4">
                    GDPR szerint a következő jogokkal rendelkezik:
                  </p>
                  <ul className="space-y-3 text-[var(--color-text-secondary)]">
                    <li className="flex items-start gap-3">
                      <span className="text-[var(--color-accent)] text-xl flex-shrink-0">•</span>
                      <span><strong className="text-[var(--color-text-primary)]">Hozzáférési jog:</strong> adatai kezeléséről tájékoztatást kérhet</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-[var(--color-accent)] text-xl flex-shrink-0">•</span>
                      <span><strong className="text-[var(--color-text-primary)]">Helyesbítési jog:</strong> hibás adatok javítását kérheti</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-[var(--color-accent)] text-xl flex-shrink-0">•</span>
                      <span><strong className="text-[var(--color-text-primary)]">Törlési jog:</strong> adatai törlését kérheti bizonyos esetekben</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-[var(--color-accent)] text-xl flex-shrink-0">•</span>
                      <span><strong className="text-[var(--color-text-primary)]">Korlátozási jog:</strong> adatkezelés korlátozását kérheti</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-[var(--color-accent)] text-xl flex-shrink-0">•</span>
                      <span><strong className="text-[var(--color-text-primary)]">Adathordozhatósági jog:</strong> adatai másolását kérheti</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-[var(--color-accent)] text-xl flex-shrink-0">•</span>
                      <span><strong className="text-[var(--color-text-primary)]">Kifogásolási jog:</strong> adatkezelés ellen kifogást tehet</span>
                    </li>
                  </ul>
                </div>

                {/* 7. Jogérvényesítés */}
                <div>
                  <h2 className="text-2xl md:text-3xl font-[var(--font-serif)] font-semibold text-[var(--color-text-primary)] mb-4">
                    7. Jogérvényesítés
                  </h2>
                  <p className="text-[var(--color-text-secondary)] leading-relaxed mb-4">
                    Jogai érvényesítéséhez forduljon hozzánk az <a href="mailto:xlbutorbolt@gmail.com" className="text-[var(--color-accent)] hover:underline">xlbutorbolt@gmail.com</a> e-mail címen, 
                    vagy írjon a 4600 Kisvárda, Attila u. 61. címre.
                  </p>
                  <p className="text-[var(--color-text-secondary)] leading-relaxed">
                    Panaszt a Nemzeti Adatvédelmi és Információszabadság Hatósághoz (NAIH) 
                    tehet: 1055 Budapest, Falk Miksa utca 9-11., vagy <a href="https://www.naih.hu" target="_blank" rel="noopener noreferrer" className="text-[var(--color-accent)] hover:underline">www.naih.hu</a> weboldalon.
                  </p>
                </div>

                {/* 8. Adatvédelmi nyilatkozat módosítása */}
                <div>
                  <h2 className="text-2xl md:text-3xl font-[var(--font-serif)] font-semibold text-[var(--color-text-primary)] mb-4">
                    8. Adatvédelmi nyilatkozat módosítása
                  </h2>
                  <p className="text-[var(--color-text-secondary)] leading-relaxed">
                    Jogfenntartjuk magunknak a jogot, hogy ezt az adatvédelmi nyilatkozatot 
                    bármikor módosítsuk. A módosításokról a weboldalunkon keresztül tájékoztatjuk.
                  </p>
                </div>

                {/* Utolsó módosítás */}
                <div className="pt-8 border-t border-gray-200">
                  <p className="text-sm text-[var(--color-text-secondary)]">
                    <strong>Utolsó módosítás:</strong> {new Date().getFullYear()}. {new Date().toLocaleDateString('hu-HU', { month: 'long' })}
                  </p>
                </div>
              </motion.div>
            </div>
          </section>
        </main>
        
        <ModernFooter />
      </div>
    </>
  );
}

